import { CircleCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui-kit/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useGetLunchMenus } from '@/modules/lunch/hr-manage-lunch/hooks/use-manage-lunch';
import { LunchMenu, LunchMenusResult } from '@/modules/lunch/hr-manage-lunch/types/manage-lunch.types';
import { LunchRequest } from '../../types/lunch.types';

export interface MealWeekProps {
  /** The currently active target lunch date in YYYY-MM-DD */
  targetDate: string;
  /** Existing user requests for the week (to show checkmarks) */
  userRequests?: LunchRequest[];
  className?: string;
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const WEEKEND_DAYS = ['Friday', 'Saturday'];

/**
 * Generates the 7 days of the week surrounding the target date (Sunday through Saturday).
 */
const getWeekDays = (targetDate: string) => {
  const [year, month, day] = targetDate.split('-').map(Number);
  const target = new Date(year, month - 1, day);
  
  // Sunday is index 0
  const dayOfWeek = target.getDay();
  const sunday = new Date(target);
  sunday.setDate(target.getDate() - dayOfWeek);

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sunday);
    d.setDate(sunday.getDate() + i);
    const isoString = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
    return {
      date: d,
      isoDate: isoString,
      dayIndex: i,
      dayShort: WEEKDAY_SHORT[i],
      dayName: WEEKDAY_NAMES[i],
      dayNum: d.getDate(),
      isWeekend: WEEKEND_DAYS.includes(WEEKDAY_NAMES[i]),
      isTarget: isoString === targetDate,
      isPast: d < new Date(target.getFullYear(), target.getMonth(), target.getDate()),
    };
  });
};

/**
 * "This week" meal schedule sidebar.
 * Displays all 7 days of the current week, menus for each day, weekend notices,
 * active "Ordering" badge, and ordered status checkmarks.
 * Matches HR Module Prototype `.meal-week`.
 */
export const MealWeek = ({ targetDate, userRequests = [], className = '' }: MealWeekProps) => {
  const { t } = useTranslation();
  const { data: menuData, isLoading } = useGetLunchMenus({ pageNo: 1, pageSize: 100, filter: {} });
  const allMenus: LunchMenu[] =
    (menuData as { LunchMenus?: LunchMenusResult })?.LunchMenus?.items ?? [];

  const days = getWeekDays(targetDate);
  const firstDay = days[0].date;
  const lastDay = days[6].date;
  const dateRangeLabel = `${firstDay.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })} – ${lastDay.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

  const ordersByDate = new Map<string, LunchRequest>();
  userRequests.forEach((req) => {
    if (req.RequestedForDate) {
      ordersByDate.set(req.RequestedForDate.slice(0, 10), req);
    }
  });

  return (
    <Card className={`sticky top-20 flex flex-col shadow-sm ${className}`}>
      <CardHeader className="pb-3 md:pb-3">
        <CardTitle className="text-base font-semibold">
          {t('THIS_WEEK', { defaultValue: 'This week' })}
        </CardTitle>
        <CardDescription className="text-xs">{dateRangeLabel}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-1 p-3 pt-0 md:p-3 md:pt-0">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 7 }, (_, i) => (
              <Skeleton key={i} className="h-11 w-full rounded-md" />
            ))}
          </div>
        ) : (
          <ol className="m-0 flex list-none flex-col gap-1 p-0">
            {days.map((day) => {
              const dayMenus = allMenus.filter(
                (m) => m.WeekDay === day.dayName && m.IsActive
              );
              const order = ordersByDate.get(day.isoDate);

              return (
                <li
                  key={day.isoDate}
                  className={`flex items-start gap-3 rounded-md p-2 transition-colors ${
                    day.isTarget
                      ? 'bg-accent text-accent-foreground font-medium'
                      : day.isPast || day.isWeekend
                      ? 'text-muted-foreground'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  {/* Date badge: e.g. "SUN 1" */}
                  <div className="flex w-9 shrink-0 flex-col items-center justify-center leading-tight">
                    <span className="text-[10px] font-semibold text-muted-foreground">
                      {day.dayShort}
                    </span>
                    <span className="text-sm font-semibold tabular-nums">
                      {day.dayNum}
                    </span>
                  </div>

                  {/* Menu text preview or weekend notice */}
                  <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 pt-0.5 text-xs">
                    {day.isWeekend ? (
                      <span className="italic text-muted-foreground/80">
                        {t('NO_LUNCH', { defaultValue: 'No lunch' })}
                      </span>
                    ) : dayMenus.length > 0 ? (
                      dayMenus.map((menu) => (
                        <div key={menu.ItemId} className="truncate">
                          <span className="font-semibold">{menu.Name}</span>{' '}
                          <span className="text-muted-foreground">
                            {menu.MenuItems.join(', ')}
                          </span>
                        </div>
                      ))
                    ) : (
                      <span className="text-muted-foreground/70">
                        {t('NO_MENU_YET', { defaultValue: 'No menu yet' })}
                      </span>
                    )}
                  </div>

                  {/* Badges / checkmark */}
                  <div className="flex shrink-0 items-center self-center pl-1">
                    {order ? (
                      <span
                        title={`Ordered ${order.MenuName ?? ''}`}
                        className="text-success inline-flex"
                      >
                        <CircleCheck className="size-4" />
                        <span className="sr-only">Ordered</span>
                      </span>
                    ) : day.isTarget ? (
                      <Badge variant="outline" className="px-1.5 py-0 text-[11px] font-medium">
                        {t('ORDERING', { defaultValue: 'Ordering' })}
                      </Badge>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};
